import { useState, useEffect } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";

export default function ClientsPage() {
  const [loading, setLoading] = useState(true);
  const [households, setHouseholds] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const limit = 20;

  useEffect(() => {
    loadHouseholds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, sortField, sortDirection]);

  const loadHouseholds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append("search", search);
      if (sortField) {
        params.append("sort_by", sortField);
        params.append("sort_dir", sortDirection);
      }
      const response = await authFetch(`/households/?${params}`);
      setHouseholds(response.households);
      setTotal(response.total);
    } catch (error) {
      console.error("Error loading households:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const loadHouseholdDetail = async (householdId) => {
    setDetailLoading(true);
    try {
      const response = await authFetch(`/households/${householdId}`);
      setSelectedHousehold(response);
    } catch (error) {
      console.error("Error loading household detail:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
      case "yes":
        return { bg: "#ffebee", color: "#c62828" };
      case "medium":
        return { bg: "#fff3e0", color: "#e65100" };
      case "low":
      case "no":
        return { bg: "#e8f5e9", color: "#2e7d32" };
      default:
        return { bg: "#f5f5f5", color: "#666" };
    }
  };

  return (
    <>
      <Header showLogout={true} />
      <div style={{ padding: "40px", maxWidth: "1400px", margin: "0 auto" }}>
        <h1 style={{ marginBottom: "10px" }}>Clients</h1>
        <p style={{ color: "#666", marginBottom: "30px" }}>
          Manage household information and client relationships
        </p>

        {/* Search Bar */}
        <form onSubmit={handleSearch} style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", gap: "12px" }}>
            <input
              type="text"
              placeholder="Search by household name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                padding: "10px 16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                width: "300px",
              }}
            />
            <button
              type="submit"
              style={{
                padding: "10px 20px",
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setSearch("");
                  setPage(1);
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {/* Results count */}
        <div style={{ marginBottom: "16px", color: "#666", fontSize: "14px" }}>
          {total} household{total !== 1 ? "s" : ""} found
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            Loading households...
          </div>
        ) : (
          <>
            {/* Households Table */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "hidden",
                marginBottom: "24px",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <SortableHeader
                      label="Household"
                      field="household_last_name"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Engagement</th>
                    <SortableHeader
                      label="Priority"
                      field="priority"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Last Contacted"
                      field="last_contacted"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Follow Up"
                      field="follow_up"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Purchases"
                      field="purchased_count"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <th style={thStyle}>Members</th>
                    <th style={thStyle}>Representatives</th>
                  </tr>
                </thead>
                <tbody>
                  {households.map((h) => {
                    const priorityStyle = getPriorityColor(h.priority);
                    return (
                      <tr
                        key={h.id}
                        style={{
                          borderBottom: "1px solid #e0e0e0",
                          cursor: "pointer",
                          backgroundColor:
                            selectedHousehold?.id === h.id ? "#e3f2fd" : "transparent",
                        }}
                        onClick={() => loadHouseholdDetail(h.id)}
                        onMouseEnter={(e) => {
                          if (selectedHousehold?.id !== h.id) {
                            e.currentTarget.style.backgroundColor = "#fafafa";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedHousehold?.id !== h.id) {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }
                        }}
                      >
                        <td style={tdStyle}>
                          <strong>{h.household_last_name || "-"}</strong>
                        </td>
                        <td style={tdStyle}>{h.client_type || "-"}</td>
                        <td style={tdStyle}>{h.engagement_level || "-"}</td>
                        <td style={tdStyle}>
                          {h.priority && (
                            <span
                              style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "500",
                                backgroundColor: priorityStyle.bg,
                                color: priorityStyle.color,
                              }}
                            >
                              {h.priority}
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>{formatDate(h.last_contacted)}</td>
                        <td style={tdStyle}>{formatDate(h.follow_up)}</td>
                        <td style={tdStyle}>{h.purchased_count || 0}</td>
                        <td style={tdStyle}>{h.member_count || 0}</td>
                        <td style={tdStyle}>
                          {h.representatives?.length > 0
                            ? h.representatives.join(", ")
                            : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    ...paginationBtnStyle,
                    opacity: page === 1 ? 0.5 : 1,
                    cursor: page === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </button>
                <span style={{ color: "#666" }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{
                    ...paginationBtnStyle,
                    opacity: page === totalPages ? 0.5 : 1,
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Detail Panel */}
        {selectedHousehold && (
          <HouseholdDetailPanel
            household={selectedHousehold}
            loading={detailLoading}
            onClose={() => setSelectedHousehold(null)}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
          />
        )}
      </div>
    </>
  );
}

function HouseholdDetailPanel({
  household,
  loading,
  onClose,
  formatDate,
  formatCurrency,
}) {
  if (loading) {
    return (
      <div style={panelOverlayStyle}>
        <div style={panelStyle}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            Loading household details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={panelOverlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingBottom: "16px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <h2 style={{ margin: 0 }}>{household.household_last_name} Household</h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            &times;
          </button>
        </div>

        {/* Overview */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Overview</h3>
          <div style={gridStyle}>
            <div style={fieldStyle}>
              <span style={labelStyle}>Client Type</span>
              <span>{household.client_type || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Engagement Level</span>
              <span>{household.engagement_level || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Priority</span>
              <span>{household.priority || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Purchases</span>
              <span>{household.purchased_count || 0}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Last Contacted</span>
              <span>{formatDate(household.last_contacted)}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Follow Up</span>
              <span>{formatDate(household.follow_up)}</span>
            </div>
          </div>
        </div>

        {/* Financial Info */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Financial Information</h3>
          <div style={gridStyle}>
            <div style={fieldStyle}>
              <span style={labelStyle}>Net Worth</span>
              <span>{formatCurrency(household.household_net_worth)}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Avg Equity Investment</span>
              <span>{formatCurrency(household.average_equity_investment)}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Avg Purchase Price</span>
              <span>{formatCurrency(household.average_purchase_price)}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Avg Deal LTV</span>
              <span>
                {household.average_deal_ltv
                  ? `${(household.average_deal_ltv * 100).toFixed(1)}%`
                  : "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Members */}
        {household.members?.length > 0 && (
          <div style={{ marginBottom: "24px" }}>
            <h3 style={sectionHeaderStyle}>
              Members ({household.members.length})
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {household.members.map((member) => (
                <div
                  key={member.id}
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "6px",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <strong>
                      {member.first_name} {member.last_name}
                    </strong>
                    {member.household_member_status && (
                      <span
                        style={{
                          fontSize: "11px",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          backgroundColor:
                            member.household_member_status === "Primary"
                              ? "#e3f2fd"
                              : "#f5f5f5",
                          color:
                            member.household_member_status === "Primary"
                              ? "#1565c0"
                              : "#666",
                        }}
                      >
                        {member.household_member_status}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px",
                      fontSize: "13px",
                      color: "#666",
                    }}
                  >
                    {member.email && <div>{member.email}</div>}
                    {member.phone_number && <div>{member.phone_number}</div>}
                    {member.occupation && <div>{member.occupation}</div>}
                    {member.job_title && <div>{member.job_title}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Representatives */}
        {household.representatives?.length > 0 && (
          <div style={{ marginBottom: "24px" }}>
            <h3 style={sectionHeaderStyle}>
              Representatives ({household.representatives.length})
            </h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {household.representatives.map((rep) => (
                <span
                  key={rep.id}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#e8f5e9",
                    borderRadius: "16px",
                    fontSize: "13px",
                    color: "#2e7d32",
                  }}
                >
                  {rep.first_name} {rep.last_name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {household.notes && (
          <div style={{ marginBottom: "24px" }}>
            <h3 style={sectionHeaderStyle}>Notes</h3>
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#fffde7",
                borderRadius: "6px",
                border: "1px solid #fff59d",
                whiteSpace: "pre-wrap",
              }}
            >
              {household.notes}
            </div>
          </div>
        )}

        {/* Comments */}
        {household.comments?.length > 0 && (
          <div>
            <h3 style={sectionHeaderStyle}>
              Comments ({household.comments.length})
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              {household.comments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "6px",
                    borderLeft: "3px solid #1976d2",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    <span style={{ fontWeight: "500" }}>
                      {comment.author
                        ? `${comment.author.first_name} ${comment.author.last_name}`
                        : "Unknown"}
                    </span>
                    <span>{formatDate(comment.created_time)}</span>
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {comment.comment_text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles
const thStyle = {
  textAlign: "left",
  padding: "12px 16px",
  fontWeight: "600",
  fontSize: "13px",
  color: "#333",
  borderBottom: "2px solid #e0e0e0",
};

const tdStyle = {
  padding: "12px 16px",
};

const paginationBtnStyle = {
  padding: "8px 16px",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ddd",
  borderRadius: "6px",
  fontSize: "14px",
};

const panelOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "flex-end",
  zIndex: 1000,
};

const panelStyle = {
  width: "600px",
  maxWidth: "90vw",
  height: "100%",
  backgroundColor: "#fff",
  padding: "24px",
  overflowY: "auto",
  boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.1)",
};

const sectionHeaderStyle = {
  fontSize: "16px",
  fontWeight: "600",
  marginBottom: "12px",
  color: "#333",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "12px",
};

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const labelStyle = {
  fontSize: "12px",
  color: "#666",
  fontWeight: "500",
};

function SortableHeader({ label, field, sortField, sortDirection, onSort }) {
  const isActive = sortField === field;

  return (
    <th
      style={{
        ...thStyle,
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={() => onSort(field)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {label}
        <span
          style={{
            fontSize: "10px",
            color: isActive ? "#1976d2" : "#999",
          }}
        >
          {isActive ? (sortDirection === "asc" ? "▲" : "▼") : "▲▼"}
        </span>
      </div>
    </th>
  );
}
